import { useAuth } from './useAuth';
import { useTranslation } from '@/i18n';

/**
 * Shown to a user who authenticated successfully but holds no permissions at all.
 *
 * This is the state an SSO-provisioned user lands in when their tenant has no default
 * role configured. Before this screen existed they were routed to an unguarded client
 * route and saw an empty sidebar with silently failing data calls, which reads as a
 * broken application rather than an account that still needs setting up.
 *
 * It is deliberately a hard stop rather than a degraded view: a user with no permissions
 * has nothing to do in the app, and sending them onward only produces 403s.
 */
export const AccountNotConfigured = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const email = user?.profile?.email ?? user?.profile?.preferred_username ?? null;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        backgroundColor: '#f5f6f8',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '480px',
          width: '100%',
          backgroundColor: '#fff',
          borderRadius: '8px',
          border: '1px solid #e3e5e8',
          padding: '32px',
          textAlign: 'center',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: '48px',
            height: '48px',
            margin: '0 auto 20px',
            borderRadius: '50%',
            backgroundColor: '#fff4e5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            lineHeight: 1,
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#b76e00" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v5" strokeLinecap="round" />
            <path d="M12 16.5v.01" strokeLinecap="round" />
          </svg>
        </div>

        <h1 style={{ margin: '0 0 12px', fontSize: '20px', fontWeight: 600, color: '#1a1d21' }}>
          {t('Auth:AccountNotSetUpTitle', { defaultValue: 'Your account isn’t set up yet' })}
        </h1>

        <p style={{ margin: '0 0 8px', fontSize: '14px', lineHeight: 1.6, color: '#4a4f55' }}>
          {t('Auth:AccountNotSetUpBody', { defaultValue: 'You signed in successfully, but your account hasn’t been given access to anything in mot-r yet. An administrator at your organisation needs to assign your role before you can continue.' })}
        </p>

        {email && (
          <p style={{ margin: '0 0 24px', fontSize: '13px', color: '#6b7178' }}>
            {t('Auth:SignedInAs', { defaultValue: 'Signed in as' })} <strong style={{ color: '#4a4f55' }}>{email}</strong>
          </p>
        )}

        <p style={{ margin: '0 0 24px', fontSize: '13px', color: '#6b7178' }}>
          {t('Auth:ContactAdministrator', { defaultValue: 'Please contact your mot-r administrator and quote the address above.' })}
        </p>

        <button
          type="button"
          onClick={() => void logout()}
          style={{
            padding: '10px 24px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#fff',
            backgroundColor: '#2b6cb0',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          {t('Common:SignOut', { defaultValue: 'Sign out' })}
        </button>
      </div>
    </div>
  );
};

export default AccountNotConfigured;
